import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../services/api';

export interface VisibleDesigner {
  _id: string;
  name: string;
  email: string;
}

export interface Collaborator {
  user: { _id: string; name: string; email: string } | string;
  status: 'pending' | 'accepted' | 'declined';
  invitedAt: string;
  respondedAt?: string;
}

interface CollaboratorsState {
  visibleDesigners: VisibleDesigner[];
  loading: boolean;
  error: string | null;
}

const initialState: CollaboratorsState = {
  visibleDesigners: [],
  loading: false,
  error: null,
};

export const fetchVisibleDesigners = createAsyncThunk(
  'collaborators/fetchVisible',
  async (projectId: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/users/designers/visible?projectId=${projectId}`);
      return res.data as VisibleDesigner[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load designers');
    }
  }
);

export const inviteCollaborator = createAsyncThunk(
  'collaborators/invite',
  async ({ projectId, userId }: { projectId: string; userId: string }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/projects/${projectId}/collaborators`, { userId });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to send invite');
    }
  }
);

export const respondToCollaboration = createAsyncThunk(
  'collaborators/respond',
  async ({ projectId, accept }: { projectId: string; accept: boolean }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/projects/${projectId}/collaborators/respond`, { accept });
      return { projectId, accept, ...res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to respond to invitation');
    }
  }
);

export const removeCollaborator = createAsyncThunk(
  'collaborators/remove',
  async ({ projectId, userId }: { projectId: string; userId: string }, { rejectWithValue }) => {
    try {
      const res = await api.delete(`/projects/${projectId}/collaborators/${userId}`);
      return { projectId, userId, ...res.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to remove collaborator');
    }
  }
);

export const updateCollaborationVisibility = createAsyncThunk(
  'collaborators/updateVisibility',
  async (visible: boolean, { rejectWithValue }) => {
    try {
      const res = await api.patch('/users/me/collaboration-visibility', { visible });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error || 'Failed to update visibility');
    }
  }
);

const collaboratorsSlice = createSlice({
  name: 'collaborators',
  initialState,
  reducers: {
    clearVisibleDesigners(state) {
      state.visibleDesigners = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVisibleDesigners.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVisibleDesigners.fulfilled, (state, action: PayloadAction<VisibleDesigner[]>) => {
        state.loading = false;
        state.visibleDesigners = action.payload;
      })
      .addCase(fetchVisibleDesigners.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(inviteCollaborator.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(respondToCollaboration.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(removeCollaborator.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearVisibleDesigners } = collaboratorsSlice.actions;
export default collaboratorsSlice.reducer;
